import React, { Suspense, useCallback, useState } from 'react';
import { ToggleButton, ToggleButtonGroup } from '@mui/material';

/**
 * 2D/3D switch for the TJP views. 2D is the default; the three.js chunk is only
 * fetched (React.lazy) once the user picks 3D. If WebGL is missing, context
 * creation fails, or the 3D chunk throws, the 2D scene is shown instead — without
 * an uncaught exception (so the CRA dev overlay stays quiet, like production).
 */
export const TJP_VIEW_KEY = 'evlx_tjp_view';
const Scene3D = React.lazy(() => import(/* webpackChunkName: "tjp-3d" */ './TwoJointPoleScene3D'));

/** Lightweight feature detect (main chunk; no three.js). */
export function hasWebGL() {
  try {
    if (typeof window === 'undefined' || !window.WebGLRenderingContext) return false;
    const c = document.createElement('canvas');
    const gl = c.getContext('webgl2', { failIfMajorPerformanceCaveat: false })
      || c.getContext('webgl', { failIfMajorPerformanceCaveat: false });
    if (!gl) return false;
    // Drop the probe context so we do not exhaust the browser's context limit.
    const ext = gl.getExtension && gl.getExtension('WEBGL_lose_context');
    if (ext) ext.loseContext();
    return true;
  } catch (_) {
    return false;
  }
}

export function useTjpViewMode() {
  const [mode, setModeState] = useState(() => {
    try {
      return window.localStorage.getItem(TJP_VIEW_KEY) === '3d' ? '3d' : '2d';
    } catch (_) {
      return '2d';
    }
  });
  const setMode = useCallback((m) => {
    const v = m === '3d' ? '3d' : '2d';
    setModeState(v);
    try { window.localStorage.setItem(TJP_VIEW_KEY, v); } catch (_) { /* ignore */ }
  }, []);
  return [mode, setMode];
}

export function TjpViewToggle({ value, onChange, webgl = true }) {
  return (
    <ToggleButtonGroup
      size="small"
      exclusive
      value={value}
      onChange={(_, v) => { if (v) onChange(v); }}
      aria-label="View"
      sx={{ '& .MuiToggleButton-root': { color: '#9fb3c8', py: 0.25, px: 1.25 } }}
    >
      <ToggleButton value="2d" aria-label="2D view">2D</ToggleButton>
      <ToggleButton value="3d" aria-label="3D view" disabled={!webgl} title={webgl ? '' : 'WebGL not available'}>3D</ToggleButton>
    </ToggleButtonGroup>
  );
}

class Fallback2D extends React.Component {
  constructor(props) { super(props); this.state = { failed: false }; }

  static getDerivedStateFromError() { return { failed: true }; }

  componentDidCatch(err) {
    // eslint-disable-next-line no-console
    console.warn('TJP 3D view failed, using 2D', err);
    if (typeof this.props.onError === 'function') this.props.onError(err);
  }

  render() { return this.state.failed ? this.props.fallback : this.props.children; }
}

/** Renders `scene2d` or the lazy 3D scene with the same props. */
export default function TjpSceneView({ mode, scene2d, sceneProps }) {
  const [webgl] = useState(hasWebGL);
  const [glFailed, setGlFailed] = useState(false);
  const onGlFailure = useCallback((err) => {
    // eslint-disable-next-line no-console
    if (err) console.warn('TJP 3D WebGL unavailable, using 2D', err);
    setGlFailed(true);
  }, []);

  if (mode !== '3d' || !webgl || glFailed) return scene2d;
  return (
    <Fallback2D fallback={scene2d} onError={onGlFailure}>
      <Suspense fallback={scene2d}>
        <Scene3D {...sceneProps} onGlFailure={onGlFailure} />
      </Suspense>
    </Fallback2D>
  );
}

/**
 * Drop-in for the 2D scene: `<TjpSwitchableScene Scene2D={TwoJointPoleScene} {...props} />`
 * renders a small 2D/3D toggle bar plus the chosen view, both fed the same props.
 */
export function TjpSwitchableScene({ Scene2D, ...props }) {
  const [mode, setMode] = useTjpViewMode();
  const [webgl] = useState(hasWebGL);
  return (
    <div style={{ position: 'relative' }}>
      {/* Above the play-area overlays (menu/paused/over/banner at z 9-10, TJP lobby at 5),
          which are siblings in the same stacking context, so the view can be switched
          from any phase. The wrapper must not create its own stacking context. */}
      <div data-testid="tjp-view-toggle" style={{ position: 'absolute', top: 6, right: 6, zIndex: 11 }}>
        <TjpViewToggle value={webgl ? mode : '2d'} onChange={setMode} webgl={webgl} />
      </div>
      <TjpSceneView mode={mode} scene2d={<Scene2D {...props} />} sceneProps={props} />
    </div>
  );
}
